/**
 * Maps gateway / SDK failure codes to human-readable checkout copy.
 * Mirrors backend {@code PaymentFailureMessageFormatter} where possible.
 */

const PAYMENT_ERROR_CODES = {
  incorrect_card_number: "The card number is incorrect. Please check and try again.",
  invalid_card_number: "The card number is invalid. Please check and try again.",
  invalid_card: "The card details are invalid. Please check and try again.",
  card_number_invalid: "The card number is invalid. Please check and try again.",
  invalid_expiry_date: "The expiry date is invalid. Please check and try again.",
  invalid_expiration_date: "The expiry date is invalid. Please check and try again.",
  expired_card: "This card has expired. Please use a different card.",
  card_expired: "This card has expired. Please use a different card.",
  invalid_cvv: "The security code (CVV) is invalid. Please check and try again.",
  invalid_cvc: "The security code (CVV) is invalid. Please check and try again.",
  incorrect_cvc: "The security code (CVV) is incorrect. Please check and try again.",
  card_declined: "Your card was declined. Please try another card or contact your bank.",
  declined: "Your card was declined. Please try another card or contact your bank.",
  do_not_honor: "Your card was declined by the bank. Please contact your bank or try another card.",
  insufficient_funds: "Insufficient funds. Please use a different card or payment method.",
  lost_card: "This card cannot be used. Please contact your bank or use another card.",
  stolen_card: "This card cannot be used. Please contact your bank or use another card.",
  restricted_card: "This card cannot be used for this payment. Please try another card.",
  authentication_required: "Additional authentication is required. Please try again.",
  three_ds_failed: "Card authentication failed. Please try again.",
  "3ds_failed": "Card authentication failed. Please try again.",
  fraud: "This payment could not be completed. Please contact your bank or try another card.",
  generic_decline: "Payment was declined. Please try again or use a different payment method.",
  processing_error: "A processing error occurred. Please try again in a few moments.",
  issuer_not_available: "Your bank is temporarily unavailable. Please try again later.",
  try_again_later: "Payment could not be processed right now. Please try again later.",
  refused: "Your card was declined. Please try another card or contact your bank.",
  not_enough_balance: "Insufficient funds. Please use a different card or payment method.",
  not_sufficient_funds: "Insufficient funds. Please use a different card or payment method.",
  cvc_declined: "The security code (CVV) is incorrect. Please check and try again.",
  payment_expired: "Payment expired before completion. Please try again.",
  unknownerror: "Could not verify this card. Check the number and try again.",
};

const canonicalCode = (value) =>
  (value || "")
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

const looksLikeMachineCode = (value) => {
  const trimmed = (value || "").toString().trim();
  if (!trimmed || trimmed.includes(" ")) return false;
  return /^[a-z0-9_]+$/.test(trimmed) && trimmed.includes("_");
};

const titleCaseFromSnake = (code) => {
  const words = code.split("_").filter(Boolean);
  if (!words.length) return null;
  return `${words.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")}.`;
};

/**
 * @param {string} codeOrMessage - Raw SDK/gateway code or message
 * @param {Function} [t] - i18n translate function
 * @param {string} [fallback] - Default when input is empty
 */
export function formatPaymentError(codeOrMessage, t, fallback) {
  const defaultMessage =
    fallback ||
    t?.("messages.error.paymentFailed") ||
    "Payment failed. Please try again.";

  if (!codeOrMessage) return defaultMessage;

  const trimmed = codeOrMessage.toString().trim();
  const code = canonicalCode(trimmed);

  const i18nKey = `messages.error.paymentCodes.${code}`;
  const translated = t?.(i18nKey);
  if (translated && translated !== i18nKey) {
    return translated;
  }

  if (PAYMENT_ERROR_CODES[code]) {
    return PAYMENT_ERROR_CODES[code];
  }

  if (looksLikeMachineCode(trimmed)) {
    return titleCaseFromSnake(code) || defaultMessage;
  }

  return trimmed;
}

export default formatPaymentError;
